import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Pagination,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AccountBalanceWallet as WalletIcon,
  Update as UpdateIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { adminWalletSubsApi } from '../services/api';
import { Subscription, SubscriptionStats, SubscriptionFormData, WalletBalance, WalletTransaction } from '../../types';

interface UserWalletData {
  id: number;
  name: string;
  email: string;
  phone: string;
  subscription_date: string;
  renewal_date: string;
  days_remaining: number;
  subscription_status: string;
  wallet_balance: number;
  last_topup_amount: number;
  last_topup_date: string;
}

const WalletAndSubs: React.FC = () => {
  const [users, setUsers] = useState<UserWalletData[]>([]);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Dialog states
  const [openUserDetails, setOpenUserDetails] = useState(false);
  const [openUpdateWallet, setOpenUpdateWallet] = useState(false);
  const [openUpdateSubs, setOpenUpdateSubs] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWalletData | null>(null);
  
  // Wallet update form
  const [topupAmount, setTopupAmount] = useState('');
  const [topupDescription, setTopupDescription] = useState('');
  const [updatingWallet, setUpdatingWallet] = useState(false);
  
  // Subscription update form
  const [subscriptionDate, setSubscriptionDate] = useState('');
  const [renewalDate, setRenewalDate] = useState('');
  const [updatingSubs, setUpdatingSubs] = useState(false);

  const loadUsers = useCallback(async (clearErrors = true) => {
    try {
      setLoading(true);
      if (clearErrors) {
        setError(null);
      }
      
      // This would be a new API endpoint that combines user, subscription, and wallet data
      const response = await adminWalletSubsApi.getAllUsersWithWallet({
        page,
        search: searchQuery,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      
      if (response.status === 'success' && response.data) {
        setUsers(response.data.users);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, statusFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = () => {
    setPage(1);
    loadUsers();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(1);
    loadUsers();
  };

  const handleUserDetails = (user: UserWalletData) => {
    setSelectedUser(user);
    setOpenUserDetails(true);
  };

  const handleUpdateWallet = (user: UserWalletData) => {
    setSelectedUser(user);
    setTopupAmount('');
    setTopupDescription('');
    setOpenUpdateWallet(true);
  };

  const handleUpdateSubs = (user: UserWalletData) => {
    setSelectedUser(user);
    setSubscriptionDate(user.subscription_date !== 'N/A' ? user.subscription_date : '');
    setRenewalDate(user.renewal_date !== 'N/A' ? user.renewal_date : '');
    setOpenUpdateSubs(true);
  };

  const handleSubscriptionUpdate = async () => {
    if (!selectedUser) return;
    
    try {
      setUpdatingSubs(true);
      setError(null);
      
      const response = await adminWalletSubsApi.updateSubscription({
        user_id: selectedUser.id,
        subscription_date: subscriptionDate || undefined,
        renewal_date: renewalDate || undefined
      });
      
      if (response.status === 'success') {
        setSuccess(`Subscription updated for ${selectedUser.name}`);
        setOpenUpdateSubs(false);
        setSubscriptionDate('');
        setRenewalDate('');
        
        // Refresh the users list
        loadUsers(false);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      } else {
        setError(response.message || 'Failed to update subscription');
      }
      
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update subscription');
    } finally {
      setUpdatingSubs(false);
    }
  };

  const handleWalletTopup = async () => {
    if (!selectedUser || !topupAmount) return;
    
    try {
      setUpdatingWallet(true);
      setError(null); // Clear any previous errors
      
      const response = await adminWalletSubsApi.topupWallet({
        user_id: selectedUser.id,
        amount: parseFloat(topupAmount),
        description: topupDescription || `Admin topup - ${topupAmount} credits added`
      });
      
      if (response.status === 'success') {
        setSuccess(`Successfully added ${topupAmount} credits to ${selectedUser.name}'s wallet`);
        setOpenUpdateWallet(false);
        setTopupAmount('');
        setTopupDescription('');
        loadUsers(false); // Refresh the list without clearing errors
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      } else {
        setError(response.message || 'Failed to update wallet');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update wallet');
    } finally {
      setUpdatingWallet(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'expired': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Wallet & Subscriptions
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage user wallets and subscription details
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name or user ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="low_balance">Low Balance</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              fullWidth
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              fullWidth
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={12} md={1}>
            <IconButton onClick={() => loadUsers()}>
              <RefreshIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Subs Date</TableCell>
                <TableCell>Renew Date</TableCell>
                <TableCell>Days Remaining</TableCell>
                <TableCell>Subs Status</TableCell>
                <TableCell>Wallet Balance</TableCell>
                <TableCell>Last Topup</TableCell>
                <TableCell>Topup Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {user.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(user.subscription_date)}</TableCell>
                  <TableCell>{formatDate(user.renewal_date)}</TableCell>
                  <TableCell>
                    <Chip
                      label={`${user.days_remaining} days`}
                      color={user.days_remaining > 30 ? 'success' : user.days_remaining > 7 ? 'warning' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.subscription_status}
                      color={getStatusColor(user.subscription_status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color={user.wallet_balance < 101 ? 'error' : 'inherit'}
                    >
                      {user.wallet_balance}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.last_topup_amount > 0 ? `+${user.last_topup_amount}` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.last_topup_date ? formatDate(user.last_topup_date) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleUserDetails(user)}
                        title="User Details"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateWallet(user)}
                        title="Update Wallet"
                      >
                        <WalletIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateSubs(user)}
                        title="Update Subscription"
                      >
                        <UpdateIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* User Details Dialog */}
      <Dialog
        open={openUserDetails}
        onClose={() => setOpenUserDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Grid container spacing={3}>
                {/* Personal Information */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Personal Information
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><PersonIcon /></ListItemIcon>
                          <ListItemText primary="Name" secondary={selectedUser.name} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Email" secondary={selectedUser.email} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Phone" secondary={selectedUser.phone} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="User ID" secondary={selectedUser.id} />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Wallet Information */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Wallet Information
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><WalletIcon /></ListItemIcon>
                          <ListItemText 
                            primary="Current Balance" 
                            secondary={`${selectedUser.wallet_balance} credits`} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Last Topup Amount" 
                            secondary={selectedUser.last_topup_amount > 0 ? `+${selectedUser.last_topup_amount} credits` : 'No topups'} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Last Topup Date" 
                            secondary={selectedUser.last_topup_date ? formatDate(selectedUser.last_topup_date) : 'Never'} 
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Subscription Information */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Subscription Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Status</Typography>
                          <Chip
                            label={selectedUser.subscription_status}
                            color={getStatusColor(selectedUser.subscription_status)}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Subscription Date</Typography>
                          <Typography variant="body2">{formatDate(selectedUser.subscription_date)}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Renewal Date</Typography>
                          <Typography variant="body2">{formatDate(selectedUser.renewal_date)}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Days Remaining</Typography>
                          <Chip
                            label={`${selectedUser.days_remaining} days`}
                            color={selectedUser.days_remaining > 30 ? 'success' : selectedUser.days_remaining > 7 ? 'warning' : 'error'}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUserDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Update Wallet Dialog */}
      <Dialog
        open={openUpdateWallet}
        onClose={() => setOpenUpdateWallet(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Wallet - {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Balance: {selectedUser?.wallet_balance} credits
            </Typography>
            <TextField
              fullWidth
              label="Topup Amount"
              type="number"
              value={topupAmount}
              onChange={(e) => setTopupAmount(e.target.value)}
              sx={{ mt: 2 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">credits</InputAdornment>
              }}
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              value={topupDescription}
              onChange={(e) => setTopupDescription(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="e.g., Admin topup - Monthly credits"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUpdateWallet(false)}>Cancel</Button>
          <Button
            onClick={handleWalletTopup}
            variant="contained"
            disabled={!topupAmount || updatingWallet}
            startIcon={updatingWallet ? <CircularProgress size={20} /> : <WalletIcon />}
          >
            {updatingWallet ? 'Adding...' : 'Add Credits'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Subscription Dialog */}
      <Dialog
        open={openUpdateSubs}
        onClose={() => setOpenUpdateSubs(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Subscription - {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Status: {selectedUser?.subscription_status}
            </Typography>
            <TextField
              fullWidth
              type="date"
              label="Subscription Date"
              value={subscriptionDate}
              onChange={(e) => setSubscriptionDate(e.target.value)}
              sx={{ mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              type="date"
              label="Renewal Date"
              value={renewalDate}
              onChange={(e) => setRenewalDate(e.target.value)}
              sx={{ mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUpdateSubs(false)}>Cancel</Button>
          <Button
            onClick={handleSubscriptionUpdate}
            variant="contained"
            disabled={updatingSubs}
            startIcon={updatingSubs ? <CircularProgress size={20} /> : <UpdateIcon />}
          >
            {updatingSubs ? 'Updating...' : 'Update Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletAndSubs;
